import { TextInput, Button, Select } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { Row, Col } from 'reactstrap';
import { useForm } from 'react-hook-form';
import { TranslationsKeys } from '../../../../locales/translations';
import { InstitutionFields, InstitutionFormData } from '../interfaces/type';
import { useValidations } from '../../../../hooks/useValidations';

type InstitutionsFormProps = Partial<InstitutionFormData> & {
  defaultValues?: Partial<InstitutionFields>;
  saving?: boolean;
  onCancel?: () => void;
  onSubmit?: (values: InstitutionFields) => void;
};

function InstitutionsForm({
  defaultValues,
  saving = false,
  onCancel,
  onSubmit,
  ...data
}: InstitutionsFormProps) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_INSTITUTIONS);

  const { msgValidations } = useValidations();

  const { control, ...form } = useForm<InstitutionFields>({ defaultValues });

  const handleSubmit = useCallback(
    (values: InstitutionFields) => {
      if (saving) {
        return;
      }
      !!onSubmit && onSubmit(values);
    },
    [saving, onSubmit],
  );

  return (
    <Row>
      <Col xs={12}>
        <TextInput
          control={control}
          name="name"
          label={t('fields.name')}
          rules={{
            required: msgValidations.required,
            minLength: {
              message: msgValidations.invalidMinLength(1),
              value: 1,
            },
          }}
        />
      </Col>
      <Col xs={12}>
        <TextInput
          control={control}
          name="code"
          label={t('fields.code')}
          rules={{
            required: msgValidations.required,
            minLength: {
              message: msgValidations.invalidMinLength(1),
              value: 1,
            },
          }}
        />
      </Col>
      <Col xs={12}>
        <Select
          control={control}
          name="country"
          label={t('fields.country')}
          isClearable={false}
          isSearchable={true}
          options={data?.countries || []}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
        <Button
          type="button"
          outlined
          disabled={saving}
          onClick={onCancel}
          text={t(`common:actions.close`)}
          fullwidth
        />
      </Col>
      <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
        <Button
          type="button"
          loading={saving}
          text={t(`common:actions.save`)}
          onClick={form.handleSubmit(handleSubmit)}
          fullwidth
        />
      </Col>
    </Row>
  );
}

export default InstitutionsForm;
